import { useState } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgentSource } from "@/api/client/hosted-agents-sources"

import {
  deleteHostedAgentSourceApi,
  downloadHostedAgentSourceApi,
  getHostedAgentSourceContentApi,
} from "@/api/client/hosted-agents-sources"
import { BookOpen, Download, FileText, Loader2, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import { useAgentSources } from "../hooks/use-agent-sources"

type Props = {
  agentRef: string
  sessionId: string | null
  canEdit: boolean
  busy?: boolean
  /** Extra classes on the trigger, for the composer's 28 px options row (#3165). */
  triggerClassName?: string
}

/** Inspect the actual sources and their scope; never imply a selected file was already read. */
export function AgentSourcesPanel({ agentRef, sessionId, canEdit, busy, triggerClassName }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const permanent = useAgentSources(agentRef)
  const task = useAgentSources(agentRef, sessionId, Boolean(sessionId))
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<HostedAgentSource | null>(null)
  const [offset, setOffset] = useState(0)
  const [removing, setRemoving] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const sources = [...(permanent.data?.sources ?? []), ...(task.data?.sources ?? [])]
  // Removing/revoking a source clears its preview even if a query has old cached text.
  const stillAvailable =
    selected &&
    !(selected.scope === "agent" ? permanent.isError : task.isError) &&
    sources.some((source) => source.id === selected.id && source.version === selected.version)
  const content = useQuery({
    queryKey: [
      "hosted-agent-source-content",
      agentRef,
      sessionId,
      selected?.id,
      selected?.version,
      offset,
    ],
    queryFn: () =>
      getHostedAgentSourceContentApi(agentRef, selected!, {
        sessionId: selected?.scope === "session" ? sessionId : null,
        offset,
      }),
    enabled: open && Boolean(stillAvailable),
    retry: false,
    staleTime: 0,
    refetchInterval: open ? 30_000 : false,
  })

  const remove = async (source: HostedAgentSource) => {
    if (!sessionId || !canEdit || busy) return
    setRemoving(source.id)
    try {
      await deleteHostedAgentSourceApi(agentRef, sessionId, source)
      setSelected(null)
      setConfirmRemove(null)
      await queryClient.invalidateQueries({
        queryKey: ["hosted-agent-sources", agentRef, sessionId],
      })
      // Do not leave extracted text in a reusable preview cache after deletion.
      queryClient.removeQueries({
        queryKey: ["hosted-agent-source-content", agentRef, sessionId, source.id],
      })
      toast.success(t("agents.sources.removed"))
    } catch {
      toast.error(t("agents.sources.remove_failed"))
    } finally {
      setRemoving(null)
    }
  }
  const download = async () => {
    if (!selected || !stillAvailable) return
    setDownloading(true)
    try {
      const blob = await downloadHostedAgentSourceApi(
        agentRef,
        selected,
        selected.scope === "session" ? sessionId : null
      )
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = selected.filename
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
      toast.error(t("agents.sources.unavailable"))
    } finally {
      setDownloading(false)
    }
  }
  const choose = (source: HostedAgentSource) => {
    setSelected(source)
    setOffset(0)
    setConfirmRemove(null)
  }
  const renderGroup = (scope: "agent" | "session") => {
    const query = scope === "agent" ? permanent : task
    const entries = query.data?.sources ?? []
    return (
      <section aria-label={t(`agents.sources.${scope}`)} className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">{t(`agents.sources.${scope}`)}</h3>
          <p className="text-muted-foreground text-xs">{t(`agents.sources.${scope}_hint`)}</p>
        </div>
        {query.isError && (
          <div className="text-sm" role="alert">
            <p>{t("agents.sources.load_failed")}</p>
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
              {t("agents.roster.retry")}
            </Button>
          </div>
        )}
        {!query.isError && query.isFetching && !query.data && (scope === "agent" || sessionId) && (
          <p className="text-muted-foreground text-sm">{t("agents.artifacts.loading")}</p>
        )}
        {!query.isError && !query.isFetching && entries.length === 0 && (
          <p className="text-muted-foreground text-sm">{t("agents.sources.empty")}</p>
        )}
        {!query.isError &&
          entries.map((source) => (
            <div key={source.id} className="rounded-lg border p-3">
              <button
                className="focus-visible:ring-ring flex min-h-11 w-full items-center gap-3 rounded-md text-left focus-visible:ring-2"
                type="button"
                onClick={() => choose(source)}
              >
                <FileText aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold break-words">
                    {source.title || source.filename}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {source.filename} · v{source.version}
                  </span>
                </span>
                <Badge variant="outline">{t(`agents.sources.${source.status}`)}</Badge>
              </button>
              {source.usage_context && (
                <p className="text-muted-foreground mt-2 text-xs whitespace-pre-wrap">
                  {source.usage_context}
                </p>
              )}
              {scope === "session" &&
                canEdit &&
                (confirmRemove === source.id ? (
                  <div className="mt-3 space-y-2" role="alert">
                    <p className="text-sm">{t("agents.sources.remove_confirm")}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={busy || removing !== null}
                        size="sm"
                        variant="destructive"
                        onClick={() => void remove(source)}
                      >
                        {t("agents.sources.remove")}
                      </Button>
                      <Button
                        disabled={removing !== null}
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmRemove(null)}
                      >
                        {t("agents.artifacts.cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    className="mt-2"
                    disabled={busy || removing !== null}
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmRemove(source.id)}
                  >
                    <Trash2 aria-hidden="true" className="size-3.5" />
                    {t("agents.sources.remove")}
                  </Button>
                ))}
            </div>
          ))}
      </section>
    )
  }
  return (
    <>
      <Button
        className={cn("min-h-10 shrink-0", triggerClassName)}
        data-testid="agent-sources-open"
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
      >
        <BookOpen aria-hidden="true" className="size-4" />
        {t("agents.sources.title")}
        {sources.length > 0 ? ` · ${sources.length}` : ""}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) {
            setSelected(null)
            setConfirmRemove(null)
            setOffset(0)
          }
        }}
      >
        <DialogContent className="max-h-[85dvh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("agents.sources.title")}</DialogTitle>
            <DialogDescription>{t("agents.sources.description")}</DialogDescription>
          </DialogHeader>
          <p className="text-muted-foreground bg-muted/40 rounded-md p-3 text-xs">
            {t("agents.sources.formats")}
          </p>
          {renderGroup("session")}
          {renderGroup("agent")}
          {selected && (
            <section
              aria-label={t("agents.sources.preview")}
              className="space-y-3 rounded-lg border p-4"
            >
              <h3 className="text-sm font-semibold">{selected.title}</h3>
              {(!stillAvailable || content.isError) && (
                <p
                  className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
                  role="alert"
                >
                  {t("agents.sources.unavailable")}
                </p>
              )}
              {stillAvailable && !content.isError && content.isPending && (
                <p className="text-muted-foreground text-sm">{t("agents.artifacts.loading")}</p>
              )}
              {stillAvailable && !content.isError && content.data && (
                <>
                  <p className="text-muted-foreground text-xs">
                    {t("agents.sources.extracted_preview")}
                  </p>
                  <pre className="max-h-64 overflow-auto font-sans text-sm break-words whitespace-pre-wrap">
                    {content.data.content}
                  </pre>
                  <div className="flex flex-wrap gap-2">
                    {offset > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setOffset(0)}>
                        {t("agents.sources.first_page")}
                      </Button>
                    )}
                    {content.data.next_offset != null && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOffset(content.data.next_offset!)}
                      >
                        {t("agents.sources.next_page")}
                      </Button>
                    )}
                    <Button
                      disabled={downloading}
                      size="sm"
                      variant="outline"
                      onClick={() => void download()}
                    >
                      {downloading ? (
                        <Loader2
                          aria-hidden="true"
                          className="size-4 animate-spin motion-reduce:animate-none"
                        />
                      ) : (
                        <Download aria-hidden="true" className="size-4" />
                      )}
                      {t("agents.sources.download")}
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
